"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import { captureMatchingContact, captureReadingContact } from "@/lib/api/chat";
import { identifyUser, trackEvent } from "@/lib/analytics";
import { CAPTURE_POINTS } from "@/lib/contact-capture-points";
import { useImpressionOnce } from "@/hooks/useImpressionOnce";

type CaptureTarget = { type: "reading" | "match"; id: string };

function parseContact(value: string): { email?: string; phone?: string } | null {
  const trimmed = value.trim();
  if (/^\S+@\S+\.\S+$/.test(trimmed)) return { email: trimmed.toLowerCase() };
  if (/^\+?\d[\d\s-]{6,}$/.test(trimmed)) return { phone: trimmed };
  return null;
}

export function ValueMomentContactCapture({
  target,
  language,
  placement,
}: {
  target: CaptureTarget;
  language: string;
  placement: string;
}) {
  const hi = language === "hi";
  const copy = contactCopy(target.type, hi);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  // rule 47: fire-once "shown" so the capture-shown → contact_captured funnel is
  // measurable (the value-moment capture had no impression event — 2026-06-15 audit).
  // Stable STRING key (target.id + placement are primitives) → fires once per
  // presentation, before any early return so hook order stays stable.
  useImpressionOnce(`contact_capture|${target.type}|${target.id}|${placement}`, () => {
    trackEvent("contact_capture_shown", {
      [`${target.type}_id`]: target.id,
      value_surface: target.type,
      placement,
      source_path: currentPath(),
    });
  });
  if (dismissed || saved) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const contact = parseContact(input);
    if (!contact) return;
    setSaving(true);
    try {
      await captureValueMomentContact(target, {
        ...contact,
        consent_marketing: marketingConsent,
      });
      if (contact.email)
        identifyUser(contact.email, { [`${target.type}_id`]: target.id, placement });
      trackEvent("contact_captured", {
        [`${target.type}_id`]: target.id,
        value_surface: target.type,
        type: contact.email ? "email" : "phone",
        placement,
        capture_point: CAPTURE_POINTS.VALUE_MOMENT,
        source_path: currentPath(),
        consent_marketing: marketingConsent,
      });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="rounded-lg border border-ish-border bg-ish-bg p-4"
      data-testid="value-moment-contact-capture"
    >
      <p className="text-[14px] font-semibold text-ish-dark">{copy.heading}</p>
      <p className="mt-1 text-[12px] leading-relaxed text-ish-text-muted">{copy.body}</p>
      <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder={hi ? "Email ya mobile number" : "Email or mobile number"}
          disabled={saving}
          className="min-w-0 flex-1 rounded px-3 py-2 text-[13px] outline-none"
          style={{ border: "1px solid var(--ish-border)", background: "var(--color-bg)" }}
        />
        <button
          type="submit"
          disabled={saving || !input.trim()}
          className="rounded px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
          style={{ background: "var(--ish-accent)" }}
        >
          {saving ? "Saving..." : hi ? "Save" : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="rounded px-3 py-2 text-[12px] font-medium text-ish-text-muted"
        >
          {hi ? "Baad me" : "Skip"}
        </button>
      </form>
      <label className="mt-2 flex items-start gap-2 text-[11px] text-ish-text-muted">
        <input
          type="checkbox"
          checked={marketingConsent}
          disabled={saving}
          onChange={(event) => setMarketingConsent(event.target.checked)}
          className="mt-0.5"
        />
        <span>{copy.optIn}</span>
      </label>
    </div>
  );
}

async function captureValueMomentContact(
  target: CaptureTarget,
  body: { email?: string; phone?: string; consent_marketing: boolean }
) {
  if (target.type === "match") return captureMatchingContact(target.id, body);
  return captureReadingContact(target.id, body);
}

function contactCopy(targetType: CaptureTarget["type"], hi: boolean) {
  if (targetType === "match") {
    return hi
      ? {
          heading: "Apna milan result save karein",
          body: "Email ya mobile dein. Hum is milan result ko aapke contact ke saath save karenge; vivah upay sirf opt-in par bhejenge.",
          optIn: "Vivah, milan aur upay ke updates WhatsApp/email par paayein.",
        }
      : {
          heading: "Save this match link",
          body: "Add email or mobile. We will attach this result to your contact; marriage remedies only go out if you opt in.",
          optIn: "Send marriage compatibility updates and remedies on WhatsApp/email.",
        };
  }
  return hi
    ? {
        heading: "Apni kundali link save karein",
        body: "Email ya mobile dein. Link wapas mil jayega; rashifal/upay sirf opt-in par bhejenge.",
        optIn: "Apni rashi ke mutaabik weekly rashifal aur upay WhatsApp/email par paayein.",
      }
    : {
        heading: "Save this kundali link",
        body: "Add email or mobile. We will save the link; rashifal/upay only goes out if you opt in.",
        optIn: "Send weekly rashifal and upay for my rashi on WhatsApp/email.",
      };
}

function currentPath(): string {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}
